/** Режим: оставить материалы/текстуры из 3DS (не перекрывать каталогом). */
export const HANDLE_MATERIAL_FROM_FILE_ID = "from-file";

export const FROM_FILE_HANDLE_MATERIAL = {
    id: HANDLE_MATERIAL_FROM_FILE_ID,
    name: "Из файла",
    color: "#B0B0B0",
    metalness: 0.45,
    roughness: 0.4,
    mapUrl: null,
    builtin: true,
    fromFile: true,
};

/** Встроенные пресеты материалов фурнитуры (ручки). */
export const BUILTIN_HANDLE_MATERIALS = [
    {
        id: "chrome",
        name: "Хром",
        color: "#C8CDD2",
        metalness: 0.92,
        roughness: 0.18,
        mapUrl: null,
        builtin: true,
    },
    {
        id: "nickel",
        name: "Никель",
        color: "#A8AEB4",
        metalness: 0.85,
        roughness: 0.28,
        mapUrl: null,
        builtin: true,
    },
    {
        id: "matte-black",
        name: "Чёрный мат",
        color: "#2A2A2A",
        metalness: 0.35,
        roughness: 0.72,
        mapUrl: null,
        builtin: true,
    },
    {
        id: "brass",
        name: "Латунь",
        color: "#C4A35A",
        metalness: 0.88,
        roughness: 0.32,
        mapUrl: null,
        builtin: true,
    },
    {
        id: "gold",
        name: "Золото",
        color: "#D4AF37",
        metalness: 0.9,
        roughness: 0.25,
        mapUrl: null,
        builtin: true,
    },
    {
        id: "white",
        name: "Белый",
        color: "#F2F2F0",
        metalness: 0.05,
        roughness: 0.55,
        mapUrl: null,
        builtin: true,
    },
];

/** @deprecated используйте BUILTIN_HANDLE_MATERIALS / createDefaultHandleMaterialCatalog */
export const HANDLE_MATERIALS = BUILTIN_HANDLE_MATERIALS;

/** По умолчанию — вид из 3DS, без пресета каталога. */
export const DEFAULT_HANDLE_MATERIAL_ID = HANDLE_MATERIAL_FROM_FILE_ID;

export const isHandleMaterialFromFile = (idOrMat) => {
    if (idOrMat == null || idOrMat === "") return true;
    if (typeof idOrMat === "string") return idOrMat === HANDLE_MATERIAL_FROM_FILE_ID;
    return Boolean(idOrMat?.fromFile || idOrMat?.id === HANDLE_MATERIAL_FROM_FILE_ID);
};

export const clamp01 = (n, fallback = 0) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return fallback;
    return Math.min(1, Math.max(0, v));
};

export const normalizeHexColor = (hex, fallback = "#C8CDD2") => {
    const raw = String(hex || "").trim();
    if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase();
    if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
        const r = raw[1];
        const g = raw[2];
        const b = raw[3];
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }
    return fallback;
};

export const createDefaultHandleMaterialCatalog = () => [
    {...FROM_FILE_HANDLE_MATERIAL},
    ...BUILTIN_HANDLE_MATERIALS.map((m) => ({...m})),
];

export const buildCustomHandleMaterial = ({
    id,
    name,
    color = "#C8CDD2",
    metalness = 0.5,
    roughness = 0.4,
    mapUrl = null,
}) => ({
    id: id || `handle-mat-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    name: String(name || "Свой").trim().slice(0, 40) || "Свой",
    color: normalizeHexColor(color),
    metalness: clamp01(metalness, 0.5),
    roughness: clamp01(roughness, 0.4),
    mapUrl: mapUrl || null,
    builtin: false,
});

export const getHandleMaterialById = (id, catalog = null) => {
    const list = Array.isArray(catalog) && catalog.length
        ? catalog
        : createDefaultHandleMaterialCatalog();
    if (isHandleMaterialFromFile(id)) {
        return list.find((m) => isHandleMaterialFromFile(m)) || FROM_FILE_HANDLE_MATERIAL;
    }
    const found = list.find((m) => m.id === id);
    if (found) return found;
    return list.find((m) => m.id === DEFAULT_HANDLE_MATERIAL_ID)
        || list[0]
        || FROM_FILE_HANDLE_MATERIAL;
};

/** Параметры для MeshStandardMaterial (+ опциональный mapUrl). */
export const resolveHandleMaterialProps = (materialId, catalog = null) => {
    const preset = getHandleMaterialById(materialId, catalog);
    if (isHandleMaterialFromFile(preset)) {
        return {
            id: HANDLE_MATERIAL_FROM_FILE_ID,
            fromFile: true,
            color: preset.color,
            metalness: preset.metalness,
            roughness: preset.roughness,
            mapUrl: null,
        };
    }
    return {
        id: preset.id,
        fromFile: false,
        color: preset.color,
        metalness: preset.metalness,
        roughness: preset.roughness,
        mapUrl: preset.mapUrl || null,
    };
};

const HANDLE_MAT_PERSIST_FIELDS = [
    "id",
    "name",
    "color",
    "metalness",
    "roughness",
    "mapUrl",
    "builtin",
];

/** Для localStorage: неизменённый builtin — компактно; правки — полные поля. */
export const serializeHandleMaterialCatalog = (catalog = []) =>
    (Array.isArray(catalog) ? catalog : [])
        .filter((m) => m?.id)
        .map((m) => {
            if (isHandleMaterialFromFile(m)) {
                return {
                    id: HANDLE_MATERIAL_FROM_FILE_ID,
                    name: FROM_FILE_HANDLE_MATERIAL.name,
                    builtin: true,
                    fromFile: true,
                };
            }
            if (m.builtin) {
                const base = BUILTIN_HANDLE_MATERIALS.find((b) => b.id === m.id);
                if (base) {
                    const sameColor = normalizeHexColor(m.color, base.color) === base.color;
                    const sameMetal = clamp01(m.metalness, base.metalness) === base.metalness;
                    const sameRough = clamp01(m.roughness, base.roughness) === base.roughness;
                    const sameMap = !m.mapUrl;
                    const sameName = !m.name || m.name === base.name;
                    if (sameColor && sameMetal && sameRough && sameMap && sameName) {
                        return {
                            id: m.id,
                            name: base.name,
                            builtin: true,
                        };
                    }
                }
            }
            const out = {};
            HANDLE_MAT_PERSIST_FIELDS.forEach((key) => {
                if (m[key] !== undefined) out[key] = m[key];
            });
            out.builtin = Boolean(m.builtin);
            return out;
        });

/**
 * Восстановить каталог. «Из файла» всегда первым.
 * Удалённые цветовые пресеты не возвращаются.
 */
export const hydrateHandleMaterialCatalog = (savedCatalog) => {
    if (!Array.isArray(savedCatalog)) {
        return createDefaultHandleMaterialCatalog();
    }

    const restored = savedCatalog
        .filter((entry) => entry?.id && !isHandleMaterialFromFile(entry))
        .map((entry) => {
            const base = BUILTIN_HANDLE_MATERIALS.find((b) => b.id === entry.id);
            if (base && entry.builtin !== false) {
                const compact = entry.color == null
                    && entry.metalness == null
                    && entry.roughness == null
                    && !entry.mapUrl;
                if (compact) {
                    return {
                        ...base,
                        name: entry.name || base.name,
                        builtin: true,
                    };
                }
                return {
                    ...base,
                    name: entry.name || base.name,
                    color: normalizeHexColor(entry.color, base.color),
                    metalness: clamp01(entry.metalness, base.metalness),
                    roughness: clamp01(entry.roughness, base.roughness),
                    mapUrl: entry.mapUrl || null,
                    builtin: true,
                };
            }
            return buildCustomHandleMaterial({
                id: entry.id,
                name: entry.name,
                color: entry.color,
                metalness: entry.metalness,
                roughness: entry.roughness,
                mapUrl: entry.mapUrl,
            });
        });

    return [{...FROM_FILE_HANDLE_MATERIAL}, ...restored];
};

/** Прочитать картинку покрытия как data URL (опционально). */
export const readHandleMapFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        if (!file || !String(file.type || "").startsWith("image/")) {
            reject(new Error("Нужен файл изображения"));
            return;
        }
        if (file.size > 4 * 1024 * 1024) {
            reject(new Error("Картинка больше 4 МБ"));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
        reader.readAsDataURL(file);
    });
